import React from "react";
import { AbsoluteFill, useCurrentFrame, interpolate, spring, useVideoConfig, Img, Easing } from "remotion";

// 效果 71: 图片过渡 - 交叉溶解
export const ImageCrossDissolve: React.FC<{
  image1Url: string;
  image2Url: string;
  transitionDuration?: number;
}> = ({ image1Url, image2Url, transitionDuration = 30 }) => {
  const frame = useCurrentFrame();
  const opacity1 = interpolate(frame, [60, 60 + transitionDuration], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const opacity2 = interpolate(frame, [60, 60 + transitionDuration], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill style={{ backgroundColor: "#000" }}>
      <Img
        src={image1Url}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          position: "absolute",
          opacity: opacity1,
        }}
      />
      <Img
        src={image2Url}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          position: "absolute",
          opacity: opacity2,
        }}
      />
    </AbsoluteFill>
  );
};

// 效果 72: 图片过渡 - 滑动覆盖
export const ImageSlideOverlay: React.FC<{
  image1Url: string;
  image2Url: string;
  direction?: "left" | "right" | "top" | "bottom";
}> = ({ image1Url, image2Url, direction = "left" }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const progress = spring({
    frame: frame - 60,
    fps,
    config: { damping: 20, stiffness: 100 },
  });

  const getTransform = () => {
    const offset = (1 - progress) * 100;
    switch (direction) {
      case "left":
        return `translateX(${offset}%)`;
      case "right":
        return `translateX(-${offset}%)`;
      case "top":
        return `translateY(${offset}%)`;
      case "bottom":
        return `translateY(-${offset}%)`;
      default:
        return "none";
    }
  };

  return (
    <AbsoluteFill style={{ backgroundColor: "#000", overflow: "hidden" }}>
      <Img
        src={image1Url}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
      <Img
        src={image2Url}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          position: "absolute",
          transform: getTransform(),
        }}
      />
    </AbsoluteFill>
  );
};

// 效果 73: 图片过渡 - 百叶窗
export const ImageBlinds: React.FC<{
  image1Url: string;
  image2Url: string;
  blindsCount?: number;
  direction?: "horizontal" | "vertical";
}> = ({ image1Url, image2Url, blindsCount = 8, direction = "horizontal" }) => {
  const frame = useCurrentFrame();
  const progress = interpolate(frame, [60, 90], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill style={{ backgroundColor: "#000", overflow: "hidden" }}>
      <Img
        src={image1Url}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
      {Array.from({ length: blindsCount }, (_, i) => {
        const delay = i * 2;
        const currentProgress = interpolate(
          frame,
          [60 + delay, 90 + delay],
          [0, 1],
          { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
        );

        const isOpen = currentProgress > 0.5;
        const size = direction === "horizontal"
          ? `${100 / blindsCount}%`
          : "100%";
        const position = direction === "horizontal"
          ? `${(i * 100) / blindsCount}%`
          : "0";
        const height = direction === "horizontal" ? "100%" : `${100 / blindsCount}%`;
        const top = direction === "horizontal" ? "0" : `${(i * 100) / blindsCount}%`;

        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: position,
              top,
              width: size,
              height,
              overflow: "hidden",
            }}
          >
            <Img
              src={image2Url}
              style={{
                width: "100vw",
                height: "100vh",
                objectFit: "cover",
                position: "absolute",
                left: direction === "horizontal" ? `-${(i * 100) / blindsCount}vw` : "0",
                top: direction === "vertical" ? `-${(i * 100) / blindsCount}vh` : "0",
                transform: direction === "horizontal"
                  ? `scaleX(${isOpen ? 1 : 0})`
                  : `scaleY(${isOpen ? 1 : 0})`,
              }}
            />
          </div>
        );
      })}
    </AbsoluteFill>
  );
};

// 效果 74: 图片过渡 - 方块网格
export const ImageGridTransition: React.FC<{
  image1Url: string;
  image2Url: string;
  gridSize?: number;
}> = ({ image1Url, image2Url, gridSize = 5 }) => {
  const frame = useCurrentFrame();

  return (
    <AbsoluteFill style={{ backgroundColor: "#000" }}>
      <Img
        src={image1Url}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
      <div
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          display: "grid",
          gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
        }}
      >
        {Array.from({ length: gridSize * gridSize }, (_, i) => {
          const row = Math.floor(i / gridSize);
          const col = i % gridSize;
          const delay = (row + col) * 3;
          const progress = interpolate(
            frame,
            [60 + delay, 90 + delay],
            [0, 1],
            { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
          );

          return (
            <div
              key={i}
              style={{
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Img
                src={image2Url}
                style={{
                  position: "absolute",
                  width: `${gridSize * 100}%`,
                  height: `${gridSize * 100}%`,
                  left: `-${col * 100}%`,
                  top: `-${row * 100}%`,
                  objectFit: "cover",
                  opacity: progress,
                }}
              />
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

// 效果 75: 图片过渡 - 圆形扩散
export const ImageCircleExpand: React.FC<{
  image1Url: string;
  image2Url: string;
}> = ({ image1Url, image2Url }) => {
  const frame = useCurrentFrame();
  const progress = interpolate(frame, [60, 100], [0, 150], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill style={{ backgroundColor: "#000" }}>
      <Img
        src={image1Url}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
      <div
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          clipPath: `circle(${progress}% at 50% 50%)`,
        }}
      >
        <Img
          src={image2Url}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      </div>
    </AbsoluteFill>
  );
};

// 效果 76: 图片过渡 - 擦除效果
export const ImageWipe: React.FC<{
  image1Url: string;
  image2Url: string;
  direction?: "left" | "right" | "top" | "bottom";
}> = ({ image1Url, image2Url, direction = "left" }) => {
  const frame = useCurrentFrame();
  const progress = interpolate(frame, [60, 100], [0, 100], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const getClipPath = () => {
    switch (direction) {
      case "left":
        return `inset(0 ${100 - progress}% 0 0)`;
      case "right":
        return `inset(0 0 0 ${100 - progress}%)`;
      case "top":
        return `inset(${100 - progress}% 0 0 0)`;
      case "bottom":
        return `inset(0 0 ${100 - progress}% 0)`;
      default:
        return "none";
    }
  };

  return (
    <AbsoluteFill style={{ backgroundColor: "#000" }}>
      <Img
        src={image1Url}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
      <div style={{ position: "absolute", width: "100%", height: "100%", clipPath: getClipPath() }}>
        <Img
          src={image2Url}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      </div>
    </AbsoluteFill>
  );
};

// 效果 77: 图片过渡 - 旋转进入
export const ImageRotateIn: React.FC<{
  image1Url: string;
  image2Url: string;
}> = ({ image1Url, image2Url }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const progress = spring({
    frame: frame - 60,
    fps,
    config: { damping: 15, stiffness: 100 },
  });

  const rotation = interpolate(progress, [0, 1], [90, 0]);
  const scale = interpolate(progress, [0, 0.5, 1], [0.5, 1.1, 1]);

  return (
    <AbsoluteFill style={{ backgroundColor: "#000", overflow: "hidden" }}>
      <Img
        src={image1Url}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
      <div
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          transform: `perspective(1000px) rotateY(${rotation}deg) scale(${scale})`,
        }}
      >
        <Img
          src={image2Url}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      </div>
    </AbsoluteFill>
  );
};

// 效果 78: 图片过渡 - 翻页效果
// 优化点:
//   1. 修正语义:从 image1 翻到 image2(原先首帧显示 image2,与"翻页揭晓新图"的直觉相反)
//   2. 正反双面:页面正面显示 image1,反面显示 image2,翻到一半时可见真实书页
//   3. 使用 useVideoConfig 自适应任意时长,不再硬编码 [60,100] 帧
//   4. 缓动曲线 + 前后静帧,节奏更接近真实翻书动作
//   5. 书本氛围:暗色背景、装订线阴影、暗角,增强场景沉浸感
//   6. 翻页过程中正反两面均有"弯折阴影 + 边缘高光",带来纸张立体感
//   7. 动态投影随翻页角度变化,在下方页面上留下翻页痕迹
//   8. 底层 image2 加入 Ken Burns 与暗化蒙版,翻页完成时恢复明亮
//   9. 可配置参数:翻页起始帧、持续帧、方向(左/右翻)、是否启用书本氛围
export const ImagePageFlip: React.FC<{
  image1Url: string;
  image2Url: string;
  /** 翻页起始帧。默认约 20% 处 */
  flipStart?: number;
  /** 翻页持续帧数。默认约 55% 时长 */
  flipDuration?: number;
  /** 翻页方向:"left" = 向左翻(右手翻书),"right" = 向右翻 */
  direction?: "left" | "right";
  /** 是否启用书本氛围(暗底、装订线、暗角)。默认 true */
  bookAmbience?: boolean;
}> = ({
  image1Url,
  image2Url,
  flipStart,
  flipDuration,
  direction = "left",
  bookAmbience = true,
}) => {
  const frame = useCurrentFrame();
  const { durationInFrames, width } = useVideoConfig();

  const start = flipStart ?? Math.round(durationInFrames * 0.2);
  const dur =
    flipDuration ?? Math.max(24, Math.round(durationInFrames * 0.55));
  const end = Math.min(durationInFrames - 1, start + dur);
  const mid = (start + end) / 2;

  const easeInOut = Easing.bezier(0.65, 0, 0.35, 1);
  const easeOut = Easing.out(Easing.cubic);

  // 翻页角度 0 -> 180
  const angle = interpolate(frame, [start, end], [0, 180], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: easeInOut,
  });

  // 翻页弯折强度:0 -> 1 -> 0,中点最强
  const bend = Math.sin((Math.min(Math.max(angle, 0), 180) / 180) * Math.PI);

  // 正面 image1 的 Ken Burns:缓慢推进,避免静止
  const kb1 = interpolate(frame, [0, end], [1.0, 1.06], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: easeOut,
  });
  // 反面 image2 的轻微推进,与底层形成连贯
  const kb2 = interpolate(frame, [start, end], [1.05, 1.01], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: easeInOut,
  });
  // 底层 image2 的 Ken Burns:翻页前偏大、过渡时收回、完成后继续推进
  const bgScale2 = interpolate(
    frame,
    [start, end, durationInFrames - 1],
    [1.04, 1.0, 1.05],
    {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
      easing: easeOut,
    }
  );

  // 底层 image2 暗化蒙版:翻页前偏暗,翻页完成恢复明亮
  const bgDim = interpolate(frame, [start, mid, end], [0.5, 0.28, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: easeInOut,
  });

  const rotateSign = direction === "left" ? -1 : 1;
  const transformOrigin = direction === "left" ? "left center" : "right center";
  const bindingSide = direction === "left" ? "to right" : "to left";
  const outerSide = direction === "left" ? "to left" : "to right";

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#0f0b06",
        perspective: `${Math.max(1800, width * 1.6)}px`,
        perspectiveOrigin: "center",
        overflow: "hidden",
      }}
    >
      {/* 书本氛围底色 */}
      {bookAmbience && (
        <AbsoluteFill
          style={{
            background:
              "radial-gradient(ellipse at center, #1a140c 0%, #0a0703 80%)",
          }}
        />
      )}

      {/* 底层:翻页后要露出的 image2 */}
      <AbsoluteFill style={{ overflow: "hidden" }}>
        <Img
          src={image2Url}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transform: `scale(${bgScale2})`,
            willChange: "transform",
          }}
        />
        {/* 翻页前/中轻微压暗,翻完恢复 */}
        <AbsoluteFill
          style={{
            backgroundColor: "#000",
            opacity: bgDim,
            pointerEvents: "none",
          }}
        />
      </AbsoluteFill>

      {/* 动态投影:翻页进行中,在底层投下柔和影子 */}
      {bend > 0.02 && (
        <AbsoluteFill
          style={{
            background: `linear-gradient(${outerSide}, rgba(0,0,0,${
              0.35 * bend
            }) 0%, rgba(0,0,0,0) ${30 + bend * 25}%)`,
            pointerEvents: "none",
          }}
        />
      )}

      {/* 翻页本体:正面 image1、反面 image2 */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          transformStyle: "preserve-3d",
          transform: `rotateY(${rotateSign * angle}deg)`,
          transformOrigin,
          willChange: "transform",
          boxShadow: `0 ${12 + bend * 30}px ${
            40 + bend * 60
          }px rgba(0,0,0,${0.25 + bend * 0.35})`,
        }}
      >
        {/* 正面 = image1 */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            overflow: "hidden",
          }}
        >
          <Img
            src={image1Url}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transform: `scale(${kb1})`,
              willChange: "transform",
            }}
          />
          {/* 装订侧弯折阴影 */}
          <AbsoluteFill
            style={{
              background: `linear-gradient(${bindingSide}, rgba(0,0,0,${
                0.55 * bend
              }) 0%, rgba(0,0,0,0) 35%)`,
              pointerEvents: "none",
            }}
          />
          {/* 外缘高光,模拟页面弯起时的反光 */}
          <AbsoluteFill
            style={{
              background: `linear-gradient(${outerSide}, rgba(255,255,255,${
                0.14 * bend
              }) 0%, rgba(255,255,255,0) 25%)`,
              pointerEvents: "none",
              mixBlendMode: "screen",
            }}
          />
        </div>

        {/* 反面 = image2(翻到一半后可见,内容与底层连贯) */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
            overflow: "hidden",
          }}
        >
          <Img
            src={image2Url}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transform: `scale(${kb2})`,
              willChange: "transform",
            }}
          />
          {/* 反面装订侧阴影(方向镜像) */}
          <AbsoluteFill
            style={{
              background: `linear-gradient(${outerSide}, rgba(0,0,0,${
                0.5 * bend
              }) 0%, rgba(0,0,0,0) 35%)`,
              pointerEvents: "none",
            }}
          />
          {/* 反面微暗整体压色,区分正反面 */}
          <AbsoluteFill
            style={{
              backgroundColor: "#000",
              opacity: 0.08,
              pointerEvents: "none",
            }}
          />
        </div>
      </div>

      {/* 装订线暗影(书脊效果) */}
      {bookAmbience && (
        <AbsoluteFill
          style={{
            background: `linear-gradient(${bindingSide}, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 7%)`,
            pointerEvents: "none",
          }}
        />
      )}

      {/* 暗角聚焦 */}
      {bookAmbience && (
        <AbsoluteFill
          style={{
            boxShadow: "inset 0 0 180px rgba(0,0,0,0.6)",
            pointerEvents: "none",
          }}
        />
      )}
    </AbsoluteFill>
  );
};

// 效果 79: 图片过渡 - 缩放交叉 (Zoom Cross)
// 优化点:
//   1. 使用 useVideoConfig 自适应任意时长(不再硬编码 60/120 帧)
//   2. 前段做 Ken Burns 慢推,避免静止浪费画面
//   3. 过渡使用缓动曲线 + 运动模糊,交叉更顺滑有电影感
//   4. 中段加入柔和高光(flash)与动态晕影(vignette),掩饰过渡痕迹
//   5. 完成后 image2 继续轻微推进,保持画面活感
export const ImageZoomCross: React.FC<{
  image1Url: string;
  image2Url: string;
  /** 过渡开始帧。默认约 45% 处 */
  transitionStart?: number;
  /** 过渡持续帧数。默认约 45% 时长 */
  transitionDuration?: number;
  /** 是否启用运动模糊。默认 true */
  motionBlur?: boolean;
  /** 是否启用中段高光。默认 true */
  flash?: boolean;
  /** 是否启用动态晕影。默认 true */
  vignette?: boolean;
}> = ({
  image1Url,
  image2Url,
  transitionStart,
  transitionDuration,
  motionBlur = true,
  flash = true,
  vignette = true,
}) => {
  const frame = useCurrentFrame();
  const { durationInFrames } = useVideoConfig();

  const tStart = transitionStart ?? Math.round(durationInFrames * 0.45);
  const tDur =
    transitionDuration ?? Math.max(24, Math.round(durationInFrames * 0.45));
  const tEnd = Math.min(durationInFrames - 1, tStart + tDur);
  const tMid = (tStart + tEnd) / 2;

  const easeInOut = Easing.bezier(0.65, 0, 0.35, 1);
  const easeOut = Easing.out(Easing.cubic);

  // image1:前段缓慢推进(Ken Burns),过渡段继续放大并淡出
  const preScale1 = interpolate(frame, [0, tStart], [1, 1.08], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: easeOut,
  });
  const zoomScale1 = interpolate(frame, [tStart, tEnd], [1.08, 1.45], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: easeInOut,
  });
  const scale1 = frame < tStart ? preScale1 : zoomScale1;
  const opacity1 = interpolate(frame, [tStart, tEnd], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: easeInOut,
  });
  const blur1 = motionBlur
    ? interpolate(frame, [tStart, tMid, tEnd], [0, 6, 12], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
        easing: easeInOut,
      })
    : 0;

  // image2:从略小带模糊进入,推进到 scale 1,完成后继续微推
  const inScale2 = interpolate(frame, [tStart, tEnd], [0.78, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: easeInOut,
  });
  const postScale2 = interpolate(
    frame,
    [tEnd, durationInFrames - 1],
    [1, 1.06],
    {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
      easing: easeOut,
    }
  );
  const scale2 = frame > tEnd ? postScale2 : inScale2;
  const opacity2 = interpolate(frame, [tStart, tEnd], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: easeInOut,
  });
  const blur2 = motionBlur
    ? interpolate(frame, [tStart, tMid, tEnd], [12, 6, 0], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
        easing: easeInOut,
      })
    : 0;

  // 中段柔和高光:在过渡峰值掩饰切换痕迹
  const flashOpacity = flash
    ? interpolate(frame, [tStart, tMid, tEnd], [0, 0.35, 0], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
        easing: easeInOut,
      })
    : 0;

  // 动态晕影:过渡段更强,增强镜头聚焦感
  const vignetteStrength = vignette
    ? interpolate(frame, [tStart, tMid, tEnd], [0.25, 0.5, 0.25], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
        easing: easeInOut,
      })
    : 0;

  return (
    <AbsoluteFill style={{ backgroundColor: "#000", overflow: "hidden" }}>
      <Img
        src={image1Url}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          position: "absolute",
          transform: `scale(${scale1})`,
          opacity: opacity1,
          filter: blur1 > 0.01 ? `blur(${blur1}px)` : undefined,
          willChange: "transform, opacity, filter",
        }}
      />
      <Img
        src={image2Url}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          position: "absolute",
          transform: `scale(${scale2})`,
          opacity: opacity2,
          filter: blur2 > 0.01 ? `blur(${blur2}px)` : undefined,
          willChange: "transform, opacity, filter",
        }}
      />
      {flash && flashOpacity > 0 && (
        <AbsoluteFill
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 65%)",
            opacity: flashOpacity,
            mixBlendMode: "screen",
            pointerEvents: "none",
          }}
        />
      )}
      {vignette && (
        <AbsoluteFill
          style={{
            background: `radial-gradient(ellipse at center, transparent 45%, rgba(0,0,0,${vignetteStrength}) 100%)`,
            pointerEvents: "none",
          }}
        />
      )}
    </AbsoluteFill>
  );
};

// 效果 80: 图片过渡 - 色彩通道分离 (RGB Channel Split)
export const ImageChannelSplit: React.FC<{
  image1Url: string;
  image2Url: string;
}> = ({ image1Url, image2Url }) => {
  const frame = useCurrentFrame();
  const { durationInFrames } = useVideoConfig();

  // 整体过渡进度 0 → 1
  const progress = interpolate(
    frame,
    [0, durationInFrames - 1],
    [0, 1],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );

  // 色彩分离强度：两端为 0,中段为峰值(像冲击波)
  const splitIntensity = interpolate(
    frame,
    [
      0,
      durationInFrames * 0.2,
      durationInFrames * 0.5,
      durationInFrames * 0.8,
      durationInFrames - 1,
    ],
    [0, 70, 110, 70, 0],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );

  // 叠加高频抖动,增强故障感
  const wobble = Math.sin(frame * 0.9) * splitIntensity * 0.35;
  const offsetX = splitIntensity + wobble;
  const glitchY = Math.sin(frame * 1.6) * splitIntensity * 0.18;

  // image1 → image2 交叉淡化
  const img2Opacity = interpolate(progress, [0.2, 0.8], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // 中段轻微放大,制造冲击感
  const scale =
    1 +
    interpolate(
      frame,
      [0, durationInFrames * 0.5, durationInFrames - 1],
      [0, 0.04, 0],
      { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
    );

  // 在中段使用即将出现的 image2,使通道分离作用于目标画面
  const splitSrc = progress < 0.5 ? image1Url : image2Url;

  const imgBase: React.CSSProperties = {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    position: "absolute",
    inset: 0,
  };

  return (
    <AbsoluteFill style={{ backgroundColor: "#000", overflow: "hidden" }}>
      {/* SVG 滤镜:使用 feColorMatrix 提取单一通道 */}
      <svg
        width="0"
        height="0"
        style={{ position: "absolute", pointerEvents: "none" }}
      >
        <defs>
          <filter id="channel-r">
            <feColorMatrix
              type="matrix"
              values="1 0 0 0 0
                      0 0 0 0 0
                      0 0 0 0 0
                      0 0 0 1 0"
            />
          </filter>
          <filter id="channel-g">
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0
                      0 1 0 0 0
                      0 0 0 0 0
                      0 0 0 1 0"
            />
          </filter>
          <filter id="channel-b">
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0
                      0 0 0 0 0
                      0 0 1 0 0
                      0 0 0 1 0"
            />
          </filter>
        </defs>
      </svg>

      {/* 底层:image1 → image2 交叉淡化 */}
      <AbsoluteFill style={{ transform: `scale(${scale})` }}>
        <Img src={image1Url} style={{ ...imgBase, opacity: 1 - img2Opacity }} />
        <Img src={image2Url} style={{ ...imgBase, opacity: img2Opacity }} />
      </AbsoluteFill>

      {/* RGB 三通道分离叠加 (screen 叠加,重合时还原原色) */}
      <Img
        src={splitSrc}
        style={{
          ...imgBase,
          transform: `translate(${-offsetX}px, ${glitchY}px) scale(${scale})`,
          mixBlendMode: "screen",
          filter: "url(#channel-r)",
          opacity: splitIntensity / 110,
        }}
      />
      <Img
        src={splitSrc}
        style={{
          ...imgBase,
          transform: `translate(0px, ${-glitchY}px) scale(${scale})`,
          mixBlendMode: "screen",
          filter: "url(#channel-g)",
          opacity: splitIntensity / 110,
        }}
      />
      <Img
        src={splitSrc}
        style={{
          ...imgBase,
          transform: `translate(${offsetX}px, ${glitchY}px) scale(${scale})`,
          mixBlendMode: "screen",
          filter: "url(#channel-b)",
          opacity: splitIntensity / 110,
        }}
      />

      {/* 扫描线(只在故障强度较高时出现) */}
      <AbsoluteFill
        style={{
          background:
            "repeating-linear-gradient(0deg, rgba(255,255,255,0.06) 0px, rgba(255,255,255,0.06) 1px, transparent 1px, transparent 3px)",
          opacity: splitIntensity / 220,
          mixBlendMode: "overlay",
          pointerEvents: "none",
        }}
      />

      {/* 暗角 */}
      <AbsoluteFill
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.55) 100%)",
          pointerEvents: "none",
        }}
      />

      {/* 顶部 HUD:录制指示 + 标题 + 百分比 */}
      <div
        style={{
          position: "absolute",
          top: 32,
          left: 40,
          right: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontFamily:
            "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          color: "#fff",
          textTransform: "uppercase",
          letterSpacing: 3,
          textShadow: "0 2px 8px rgba(0,0,0,0.6)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: "#ff3b3b",
              boxShadow: "0 0 12px #ff3b3b",
              opacity: 0.6 + 0.4 * Math.abs(Math.sin(frame * 0.25)),
            }}
          />
          <span style={{ fontSize: 14, fontWeight: 600 }}>
            RGB · CHANNEL SPLIT
          </span>
        </div>
        <span style={{ fontSize: 12, opacity: 0.75, letterSpacing: 2 }}>
          {String(Math.round(progress * 100)).padStart(3, "0")}%
        </span>
      </div>

      {/* 底部 HUD:RGB 指示 + 进度条 */}
      <div
        style={{
          position: "absolute",
          bottom: 32,
          left: 40,
          right: 40,
          fontFamily:
            "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          color: "#fff",
          textShadow: "0 2px 8px rgba(0,0,0,0.6)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
            fontSize: 12,
            letterSpacing: 2,
            textTransform: "uppercase",
            opacity: 0.9,
          }}
        >
          <span style={{ color: "#ff5555" }}>● R · {Math.round(offsetX)}px</span>
          <span style={{ color: "#55ff90" }}>● G · 0px</span>
          <span style={{ color: "#5596ff" }}>● B · +{Math.round(offsetX)}px</span>
        </div>
        <div
          style={{
            height: 2,
            background: "rgba(255,255,255,0.2)",
            position: "relative",
            overflow: "hidden",
            borderRadius: 2,
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              height: "100%",
              width: `${progress * 100}%`,
              background:
                "linear-gradient(90deg, #ff3b3b 0%, #3bff7a 50%, #3b8bff 100%)",
              boxShadow: "0 0 10px rgba(255,255,255,0.5)",
            }}
          />
        </div>
      </div>
    </AbsoluteFill>
  );
};
